/**
 * Pure parsing of external article sources. Inputs are untrusted JSON: every field is checked and URLs
 * are restricted to http(s) or site-relative paths.
 */
import { asString, isRecord } from '../../lib/guards';
import { htmlToText, safeUrl } from '../../lib/html';
import type { ExternalPost } from './insight-types';

const EXCERPT_LENGTH = 180;

export function guessCategory(tags: readonly string[]): string {
  const t = tags.join(' ').toLowerCase();
  if (/property|tax|assess|appeal|valuation|cre|real estate/.test(t)) return 'property-tax-operations';
  if (/orchestrat|agent|workflow|automation/.test(t)) return 'ai-orchestration';
  if (/product|release|update|launch/.test(t)) return 'product-updates';
  if (/company|news|team|hiring/.test(t)) return 'company-news';
  return 'enterprise-ai';
}

/** Entries of /assets/insights.json: `[{ title, url, date, excerpt, category, image }]`. */
export function parseInsightsJson(json: unknown): ExternalPost[] {
  if (!Array.isArray(json)) return [];
  return json.filter(isRecord).map((item) => ({
    title: asString(item.title),
    url: safeUrl(item.url),
    date: asString(item.date),
    excerpt: asString(item.excerpt),
    category: asString(item.category),
    image: safeUrl(item.image),
    source: 'site',
  }));
}

/** Items of an rss2json response for a Medium feed. */
export function parseMediumFeed(json: unknown): ExternalPost[] {
  if (!isRecord(json) || !Array.isArray(json.items)) return [];
  return json.items.filter(isRecord).map((item) => ({
    title: asString(item.title),
    url: safeUrl(item.link),
    date: asString(item.pubDate).slice(0, 10),
    image: safeUrl(item.thumbnail),
    excerpt: `${htmlToText(asString(item.description)).slice(0, EXCERPT_LENGTH)}…`,
    category: guessCategory(Array.isArray(item.categories) ? item.categories.filter((c) => typeof c === 'string') : []),
    source: 'medium',
  }));
}

/** Drops incomplete entries and duplicate URLs, newest first. */
export function mergePosts(lists: readonly ExternalPost[][]): ExternalPost[] {
  const seen = new Set<string>();
  return lists
    .flat()
    .filter((post) => {
      if (!post.title || !post.url || seen.has(post.url)) return false;
      seen.add(post.url);
      return true;
    })
    .sort((a, b) => b.date.localeCompare(a.date));
}

/** The `@handle` of a medium.com profile URL. */
export const mediumHandle = (profileUrl: string): string | undefined => /medium\.com\/(@[^/?#]+)/.exec(profileUrl)?.[1];
